
import { Local701FormOfPayment } from 'app/calcs/customers/local701/interfaces'
import { SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { getDefaultRetirementSavingsPersonInputs } from 'app/calcs/retirementSavings'
import { Profile, updateLifeExpectancy } from 'app/redux/Profile/model'
import { Gender } from 'app/redux/Profile/model/interfaces'
import { LifeExpectancyService } from 'app/services/LifeExpectancyService'
import { Config701 } from 'config/701'
import { ColumnNames, fundOfficeEmployee } from 'config/701/ETL/interfaces'
import * as _ from 'lodash'
import * as moment from 'moment'

const genderMap = {
    M: Gender.male,
    F: Gender.female
}
const gm = (g: string) => genderMap[g] || Gender.unidentified

/**
 * Populates a profile with the information found in 'extraInfo'.
 *
 * @param profile - the profile to load.
 */
function loadProfilePresets(profile: Profile, needNEBF = true, needIO = true): Promise<Profile> {
    const ex = profile.extraInfo

    const member = profile.getHeadOfHousehold()
    member.firstName = ex[ColumnNames.memberFirstName]
    member.lastName = ex[ColumnNames.memberLastName]
    member.dob = moment.utc(ex[ColumnNames.memberDOB])
    member.gender = gm(ex[ColumnNames.memberGender])

    const sln = ex[ColumnNames.spouseLastName]
    if (sln) {
        const spouse = profile.getSpouse()
        spouse.firstName = ex[ColumnNames.spouseFirstName]
        spouse.lastName = sln
        spouse.dob = moment.utc(ex[ColumnNames.spouseDOB])
        spouse.gender = gm(ex[ColumnNames.spouseGender])
    }

    const beginPaymentsAge = moment.duration(65, 'years')

    profile.pension.local701 = {
        beginPaymentsAge,
        formOfPayment: Local701FormOfPayment.fiftyPercentHusbandAndWife,
        payRate: 50,
        monthlyBenefit: 0,
        monthlyBenefit20180531: 0,
        futureService: null,
        contributionRate: 0
    }

    if (needIO) {
        profile.pension.IO = {
            beginPaymentsAge,
            formOfPayment: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
            participatesInIO: false,
            yearsOfAMembership: null
        }
    }

    if (needNEBF) {
        profile.pension.NEBF = {
            beginPaymentsAge,
            formOfPayment: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
            currentYearsService: null
        }
    }

    loadUpgradeableData(profile)

    return updateLifeExpectancy(member)
    .then(() => {
        if (sln) {
            return updateLifeExpectancy(profile.getSpouse())
            .then(() => profile)
        }
        return profile
    })
}

function loadMemberRetirementData(profile: Profile) {
    const ex = profile.extraInfo
    do {
        const member = profile.getHeadOfHousehold()
        if (!member) { break }

        const x401k = ex[ColumnNames.X401KAcctBal];
        if (_.isUndefined(x401k)) { break }

        const p1 = 'retirement.' + member.id
        const p2 = p1 + '.fixedBalances.retirementSavings';
        if (_.has(profile, p1)) {
            const curBal = _.get(profile, p2);
            if (curBal) { break; }
            // tslint:disable-next-line:no-console
            console.log('Updated profile.' + p2 + ' to DB value: ' + x401k);
            _.set(profile, p2, Math.round(x401k));
        } else {
            _.set(profile, p1, getDefaultRetirementSavingsPersonInputs())
            // tslint:disable-next-line:no-console
            console.log('Updated profile.' + p2 + ' to DB value: ' + x401k);
            _.set(profile, p2, Math.round(x401k));
        }

    } while (false)
}

function loadUpgradeableData(profile: Profile) {
    const ex = profile.extraInfo

    // fixup for some changes in acceptable formOfPayment for Local701
    if (!_.includes(Config701.BrandHelpers.memberPensionFormsOfPayment, profile.pension.local701.formOfPayment)) {
        // tslint:disable-next-line:no-debugger
        //debugger
        profile.pension.local701.formOfPayment = Local701FormOfPayment.lifeOnly
    }

    // upgrade basic local701 data
    const futureService = moment.duration(ex[ColumnNames.futureService], 'years')
    const monthlyBenefit = ex[ColumnNames.monthlyBenefit]
    const monthlyBenefit20180531 = ex[ColumnNames.monthlyBenefit20180531]
    profile.pension.local701 = {
        ...profile.pension.local701,
        futureService,
        monthlyBenefit,
        monthlyBenefit20180531
    }

    if (!profile.pension.local701.contributionRate) {
        const isNoFundOfficeEmployee = ex[ColumnNames.fundOfficeEmployee] === 'N'
        // there is no contribution rate. Choose one
        profile.pension.local701.contributionRate = isNoFundOfficeEmployee ? fundOfficeEmployee.no : fundOfficeEmployee.yes
    }

    // pick up a 401k balance if the user has not entered one
    loadMemberRetirementData(profile)

    // tslint:disable-next-line:no-debugger
    //debugger
}

/**
 * Returns an array of the information in 'extraInfo' described by a column name.
 *
 * @param profile - the profile containing the 'extraInfo'
 * @param columnNames - a list of columns required
 */
function pickColumns(profile: Profile, columnNames: string[]): any[] {
    if (!profile.extraInfo) { return [] }
    return _.map(columnNames, (propName) => profile.extraInfo[propName])
}

export {
    ColumnNames,
    loadProfilePresets,
    pickColumns,
    loadUpgradeableData
}



// WEBPACK FOOTER //
// ./src/config/701/ETL/index.ts